import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { switchMap, take, tap, catchError, finalize } from 'rxjs/operators';
import { of, throwError } from 'rxjs';

import { AppState } from '../app.state';
import { selectIsAuthenticated, selectUserUuid } from '../user/user.selectors';
import { OrderService } from '../../services/order.service';
import { Draft } from '../../models/order.model';
import { ProductV2Variation } from '../../models/product-v2.model';
import { isCartHydrationBlocked } from '../../utils/cart-hydration.util';
import { shouldSkipCampaignCartHydration } from '../../campaign/campaign-cart-reset';

import { CartItem } from './cart.state';
import { selectCartItems } from './cart.selectors';
import { CartPersistenceService } from './cart-persistence.service';
import { CartErrorHandlerService } from './cart-error-handler.service';
import { CartValidationService } from './cart-validation.service';
import { setLoading } from './cart.actions';
export interface SyncOperation {
  id: string;
  type: 'sync' | 'load' | 'save';
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  data?: any;
}

export interface SyncQueue {
  operations: SyncOperation[];
  isProcessing: boolean;
  lastProcessed: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class CartSyncService {
  private store = inject(Store<AppState>);
  private orderService = inject(OrderService);
  private cartPersistenceService = inject(CartPersistenceService);
  private errorHandler = inject(CartErrorHandlerService);
  private validationService = inject(CartValidationService);
  
  private syncQueue: SyncQueue = {
    operations: [],
    isProcessing: false,
    lastProcessed: null
  };
  
  private readonly MAX_RETRIES = 3;
  private readonly RETRY_DELAY = 1000; // 1 second
  private readonly RETRY_DELAYS_MS = [1000, 2000, 4000] as const;
  private readonly MAX_QUEUE_SIZE = 50;
  private readonly SYNC_DEBOUNCE_TIME = 2000; // 2 seconds


  queueSyncOperation(type: SyncOperation['type'], data?: any): void {
    const operation: SyncOperation = {
      id: this.generateOperationId(),
      type,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: this.MAX_RETRIES,
      data
    };

    this.syncQueue.operations.push(operation);
    
    if (this.syncQueue.operations.length > this.MAX_QUEUE_SIZE) {
      this.syncQueue.operations = this.syncQueue.operations.slice(-this.MAX_QUEUE_SIZE);
    }
    this.debouncedProcessQueue();
  }

  syncCartToBackend(): void {
    if (isCartHydrationBlocked()) {
      return;
    }

    this.store.select(selectIsAuthenticated).pipe(take(1)).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.immediateSyncToBackend();
      } else {
        this.syncToLocalStorage().subscribe();
      }
    });
  }

  clearCartFromBackend(): void {
    if (isCartHydrationBlocked()) {
      this.cartPersistenceService.clearCartFromLocalStorage();
      return;
    }

    this.store.select(selectIsAuthenticated).pipe(take(1)).subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.immediateClearFromBackend();
      } else {
        this.cartPersistenceService.clearCartFromLocalStorage();
      }
    });
  }

  private immediateClearFromBackend(): void {
    if (isCartHydrationBlocked()) {
      return;
    }

    this.store.select(selectUserUuid).pipe(
      take(1),
      switchMap(userUuid => {
        if (!userUuid) {
          throw new Error('User UUID not found');
        }

        const draftRequest: Draft = {
          userUuid,
          draftOrderUuid: null,
          orderItems: [],
        };

        return this.orderService.createOrderDraft(draftRequest);
      }),
      tap(() => {
        console.log('✅ Cart cleared from backend successfully');
      }),
      catchError(error => {
        console.error('❌ Failed to clear cart from backend:', error);
        this.errorHandler.handleSyncError(error, 'Clear Cart from Backend');
        return of(null);
      })
    ).subscribe();
  }

  private immediateSyncToBackend(): void {
    if (isCartHydrationBlocked()) {
      return;
    }

    this.store.select(selectCartItems).pipe(
      take(1),
      switchMap((items: CartItem[]) => {
        const validationResult = this.validationService.validateCartItems(items);
        if (!validationResult.isValid) {
          console.warn('Cart validation failed before sync:', validationResult.errors);
        }

        return this.store.select(selectUserUuid).pipe(
          take(1),
          switchMap(userUuid => {
            if (!userUuid) {
              throw new Error('User UUID not found');
            }

            const draftRequest: Draft = {
              userUuid,
              draftOrderUuid: null,
              orderItems: items.map((item: CartItem) => ({
                product: item.product,
                quantity: item.quantity,
                selectedVariation: item.selectedVariation as ProductV2Variation,
              })),
            };

            return this.orderService.createOrderDraft(draftRequest);
          })
        );
      }),
      tap(() => {
        console.log('✅ Cart synced to backend successfully');
      }),
      catchError(error => {
        console.error('❌ Failed to sync cart to backend:', error);
        this.errorHandler.handleSyncError(error, 'Immediate Backend Sync');
        return of(null);
      })
    ).subscribe();
  }

  loadCartFromBackend(): void {
    if (isCartHydrationBlocked()) {
      return;
    }

    this.queueSyncOperation('load');
  }

  saveCartToLocalStorage(): void {
    this.queueSyncOperation('save');
  }

  private async processQueue(): Promise<void> {
    if (this.syncQueue.isProcessing || this.syncQueue.operations.length === 0) {
      return;
    }

    this.syncQueue.isProcessing = true;

    try {
      const operations = [...this.syncQueue.operations];
      this.syncQueue.operations = [];

      for (const operation of operations) {
        await this.executeOperation(operation);
      }

      this.syncQueue.lastProcessed = Date.now();
    } catch (error) {
      this.errorHandler.handleSyncError(error, 'Process Queue');
    } finally {
      this.syncQueue.isProcessing = false;
      
      if (this.syncQueue.operations.length > 0) {
        setTimeout(() => this.processQueue(), this.RETRY_DELAY);
      }
    }
  }

  private async executeOperation(operation: SyncOperation): Promise<void> {
    try {
      switch (operation.type) {
        case 'sync':
          await this.executeSyncOperation();
          break;
        case 'load':
          await this.executeLoadOperation();
          break;
        case 'save':
          await this.executeSaveOperation();
          break;
      }
    } catch (error) {
      await this.handleOperationError(operation, error);
    }
  }

  private async executeSyncOperation(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.store
        .select(selectIsAuthenticated)
        .pipe(
          take(1),
          switchMap(isAuthenticated => {
            if (isAuthenticated) {
              return this.syncToBackend();
            } else {
              return this.syncToLocalStorage();
            }
          }),
          tap(() => resolve()),
          catchError(error => {
            reject(error);
            return throwError(error);
          })
        )
        .subscribe();
    });
  }

  private async executeLoadOperation(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.store
        .select(selectIsAuthenticated)
        .pipe(
          take(1),
          switchMap(isAuthenticated => {
            if (isAuthenticated) {
              return this.loadFromBackend();
            } else {
              return this.loadFromLocalStorage();
            }
          }),
          tap(() => resolve()),
          catchError(error => {
            reject(error);
            return throwError(error);
          })
        )
        .subscribe();
    });
  }

  private async executeSaveOperation(): Promise<void> {
    this.cartPersistenceService.saveCartToLocalStorage();
  }

  private async handleOperationError(operation: SyncOperation, error: any): Promise<void> {
    operation.retryCount++;
    
    if (operation.retryCount <= operation.maxRetries) {
      let retryDelayMs = this.RETRY_DELAY;
      switch (operation.retryCount) {
        case 1:
          retryDelayMs = this.RETRY_DELAYS_MS[0];
          break;
        case 2:
          retryDelayMs = this.RETRY_DELAYS_MS[1];
          break;
        case 3:
          retryDelayMs = this.RETRY_DELAYS_MS[2];
          break;
      }

      setTimeout(() => {
        this.syncQueue.operations.unshift(operation);
        this.processQueue();
      }, retryDelayMs);
    } else {
      this.errorHandler.handleSyncError(error, `Operation ${operation.type} failed after ${operation.maxRetries} retries`);
    }
  }

  private syncToBackend() {
    if (isCartHydrationBlocked()) {
      return of(true);
    }

    return this.store.select(selectCartItems).pipe(
      take(1),
      switchMap((items: CartItem[]) => {
        const validationResult = this.validationService.validateCartItems(items);
        if (!validationResult.isValid) {
          console.warn('Cart validation failed before sync:', validationResult.errors);
        }

        return this.store.select(selectUserUuid).pipe(
          take(1),
          switchMap(userUuid => {
            if (!userUuid) {
              throw new Error('User UUID not found');
            }

            const draftRequest: Draft = {
              userUuid,
              draftOrderUuid: null,
              orderItems: items.map((item: CartItem) => ({
                product: item.product,
                quantity: item.quantity,
                selectedVariation: item.selectedVariation as ProductV2Variation,
              })),
            };

            return this.orderService.createOrderDraft(draftRequest);
          })
        );
      })
    );
  }

  private syncToLocalStorage() {
    return this.store.select(selectCartItems).pipe(
      take(1),
      tap((items: CartItem[]) => {
        this.cartPersistenceService.saveCartToLocalStorageDirect(items);
      })
    );
  }

  private loadFromBackend() {
    if (isCartHydrationBlocked() || shouldSkipCampaignCartHydration()) {
      return of(null);
    }

    return this.store.select(selectUserUuid).pipe(
      take(1),
      switchMap(userUuid => {
        if (!userUuid) {
          throw new Error('User UUID not found');
        }

        this.store.dispatch(setLoading({ loading: true }));

        return this.orderService.getOrderDraftByUuid(userUuid).pipe(
          take(1),
          finalize(() => this.store.dispatch(setLoading({ loading: false })))
        );
      })
    );
  }

  private loadFromLocalStorage() {
    return of(this.cartPersistenceService.loadCartFromLocalStorage());
  }

  private debouncedProcessQueue(): void {
    if (this.processQueueTimeout) {
      clearTimeout(this.processQueueTimeout);
    }
    
    this.processQueueTimeout = setTimeout(() => {
      this.processQueue();
    }, this.SYNC_DEBOUNCE_TIME);
  }

  private processQueueTimeout: any;

  private generateOperationId(): string {
    return `sync_${Date.now()}_${crypto.randomUUID()}`;
  }

  getSyncQueueStatus(): {
    queueSize: number;
    isProcessing: boolean;
    lastProcessed: number | null;
    operations: SyncOperation[];
  } {
    return {
      queueSize: this.syncQueue.operations.length,
      isProcessing: this.syncQueue.isProcessing,
      lastProcessed: this.syncQueue.lastProcessed,
      operations: [...this.syncQueue.operations]
    };
  }

  clearSyncQueue(): void {
    this.syncQueue.operations = [];
    if (this.processQueueTimeout) {
      clearTimeout(this.processQueueTimeout);
    }
  }

  forceSync(): void {
    this.clearSyncQueue();
    this.processQueue();
  }

  syncOnPageUnload(): void {
    if (this.syncQueue.isProcessing) {
      return;
    }
    
    this.forceSync();
  }

  isSyncNeeded(): boolean {
    return this.syncQueue.operations.length > 0 || this.syncQueue.isProcessing;
  }

  getSyncStats(): {
    totalOperations: number;
    pendingOperations: number;
    processingOperations: number;
    lastProcessed: number | null;
    averageProcessingTime: number;
  } {
    const now = Date.now();
    const processingOperations = this.syncQueue.operations.filter(op => 
      now - op.timestamp < 5000
    ).length;

    return {
      totalOperations: this.syncQueue.operations.length,
      pendingOperations: this.syncQueue.operations.filter(op => op.retryCount === 0).length,
      processingOperations,
      lastProcessed: this.syncQueue.lastProcessed,
      averageProcessingTime: this.calculateAverageProcessingTime()
    };
  }

  private calculateAverageProcessingTime(): number {
    return 0;
  }
}
